import type {ReactElement} from 'react';
import React from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize, TextType} from '@Components/text';
import {MultiMediaUploadSource} from '@Libraries/user-media-library';
import type {ElementData} from '@Libraries/add-media-library';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import type {StockModalProps} from '@Modals/stock-modal';
import {openStockMediaModal} from '@Modals/stock-modal';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import {openEmbedVideosEmailmakerModal} from '@Modals/embed-videos-emailmaker-input-modal';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {AddItemThumbnail} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-thumbnail';
import type {StockSources} from '@Libraries/pmw-stock-media-library';
import styles from './media-video-panel-content.module.scss';

interface VideoTypeContentProps {
  isMobile?: boolean;
  panelRef: React.RefObject<HTMLDivElement | null>;
  panelId?: string;
  uploadSource?: MultiMediaUploadSource;
  maxSelectionNumber?: number;
  showVectors?: boolean;
  addInBulk?: boolean;
  hideRecordingOptions?: boolean;
  videoQualityFiltersDisabled?: boolean;
  onAddSelected(items: ElementData[]): void;
  onAddBrandAsset?(): void;
  showEmbedVideo?: boolean;
  onAddEmbeddedVideo?(videoURL: string, thumbnailURL: string): void;
  onVideoUploadRequest?: () => void;
  onVideoUploadError?(errorMessage: string): void;
  blacklistedSources?: StockSources[];
  onBack?(): void;
  onClose(): void;
}
export function MediaVideoPanelContent({
  isMobile = false,
  uploadSource = MultiMediaUploadSource.EMAIL_UPLOAD,
  hideRecordingOptions = false,
  showVectors = true,
  onAddEmbeddedVideo = (): void => {},
  onVideoUploadRequest = (): void => {},
  onVideoUploadError = (): void => {},
  onBack = (): void => {},
  showEmbedVideo = false,
  videoQualityFiltersDisabled = false,
  onClose = (): void => {},
  ...props
}: VideoTypeContentProps): ReactElement<any> {
  const handleMyUploadsClick = (): void => {
    onClose();
    openUserMediaModal({
      hideRecordingOptions,
      uploadAcceptedMimetype: 'video/*,image/gif',
      onAddSelected: props.onAddSelected,
      onAddBrandAsset: props.onAddBrandAsset,
      maxSelectionNumber: props.maxSelectionNumber,
      defaultTab: UploadsMediaType.VIDEO,
      mediaTypesToShow: [UploadsMediaType.VIDEO],
      showVectors,
      uploadSource,
      addInBulk: props.addInBulk,
      videoQualityFiltersDisabled,
    });
  };

  const buildStockVideosModalProps = (panelId: string): StockModalProps => {
    return {
      onAddSelected: props.onAddSelected,
      tabsToShow: [StockPanelTabs.VIDEOS],
      defaultSearchTab: StockPanelTabs.VIDEOS,
      maxSelectionNumber: props.maxSelectionNumber,
      addInBulk: props.addInBulk,
      populateRecentlyAddedOnItemAdd: true,
      videoQualityFiltersDisabled,
      panelId,
      blacklistedSources: props.blacklistedSources,
    };
  };

  const handleExploreMediaClick = (): void => {
    onClose();
    openStockMediaModal(buildStockVideosModalProps('STOCK_MEDIA_VIA_MEDIA_VIDEO'));
  };

  const handleAiVideosClick = (): void => {
    onClose();
    openStockMediaModal({
      ...buildStockVideosModalProps('STOCK_PANEL_VIA_AI_VIDEOS'),
      initialAiPanel: 'ai-videos',
    });
  };

  const handleEmbedVideoClick = (): void => {
    onClose();
    openEmbedVideosEmailmakerModal({
      onAddVideo: onAddEmbeddedVideo,
      onVideoUploadRequest,
      onVideoUploadError,
      onBack,
    });
  };

  const getLargeScreenContent = (): ReactElement<any> => {
    return (
      <div ref={props.panelRef} className={`flex-row-justify-between spacing-p-t-7 spacing-p-b-7 spacing-p-l-12 spacing-p-r-12 ${styles.content}`}>
        <ClickableDiv onClick={handleMyUploadsClick} className={`hover-transition-all flex-v-row flex-center border-s-dark radius-8 ${styles.option}`}>
          <Icon
            className={styles.mediaTypeIcon}
            size={IconSize.SIZE_ICON_24}
            icon="icon-upload"
            shape={IconShape.CIRCLE}
            type={IconType.FLAT}
            flatIconType={{type: FlatIconColorType.DEFAULT, color: FlatIconColor.PRIMARY}}
          />
          <Text size={TextSize.XSMALL} type={TextType.BODY} className="spacing-m-t-2 text-center" val={window.i18next.t('pmwjs_myuploads')} />
        </ClickableDiv>

        <ClickableDiv onClick={handleExploreMediaClick} className={`hover-transition-all flex-v-row flex-center border-s-dark radius-8 ${styles.option}`}>
          <Icon
            className={styles.mediaTypeIcon}
            size={IconSize.SIZE_ICON_24}
            icon="icon-video"
            shape={IconShape.CIRCLE}
            type={IconType.FLAT}
            flatIconType={{type: FlatIconColorType.DEFAULT, color: FlatIconColor.PRIMARY}}
          />
          <Text size={TextSize.XSMALL} type={TextType.BODY} className="spacing-m-t-2 text-center" val={window.i18next.t('pmwjs_explore_media')} />
        </ClickableDiv>

        <ClickableDiv onClick={handleAiVideosClick} className={`hover-transition-all flex-v-row flex-center border-s-dark radius-8 ${styles.option}`}>
          <Icon
            className={styles.mediaTypeIcon}
            size={IconSize.SIZE_ICON_24}
            icon="icon-video-ai"
            shape={IconShape.CIRCLE}
            type={IconType.FLAT}
            flatIconType={{type: FlatIconColorType.DEFAULT, color: FlatIconColor.PRIMARY}}
          />
          <Text size={TextSize.XSMALL} type={TextType.BODY} className="spacing-m-t-2 text-center" val={window.i18next.t('pmwjs_ai_videos')} />
        </ClickableDiv>

        {showEmbedVideo ? (
          <ClickableDiv onClick={handleEmbedVideoClick} className={`hover-transition-all flex-v-row flex-center border-s-dark radius-8 ${styles.option}`}>
            <Icon
              className={styles.mediaTypeIcon}
              size={IconSize.SIZE_ICON_24}
              icon="icon-html"
              shape={IconShape.CIRCLE}
              type={IconType.FLAT}
              flatIconType={{type: FlatIconColorType.DEFAULT, color: FlatIconColor.PRIMARY}}
            />
            <Text size={TextSize.XSMALL} type={TextType.BODY} className="spacing-m-t-2 text-center" val={window.i18next.t('pmwjs_embed_videos')} />
          </ClickableDiv>
        ) : null}
      </div>
    );
  };

  const getSmallScreenContent = (): ReactElement<any> => {
    return (
      <ControlledList
        className="spacing-p-b-2 spacing-p-t-1 spacing-p-r-4 spacing-p-l-4 spacing-m-0"
        items={[
          {
            id: 'my-uploads',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            subText: '',
            className: 'spacing-m-b-3',
            text: window.i18next.t('pmwjs_myuploads'),
            onClick: handleMyUploadsClick,
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail icon="icon-upload" />,
            },
          },
          {
            id: 'explore-media',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            subText: '',
            className: 'spacing-m-b-3',
            text: window.i18next.t('pmwjs_explore_media'),
            onClick: handleExploreMediaClick,
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail icon="icon-images" />,
            },
          },
          {
            id: 'ai-videos',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            subText: '',
            className: 'spacing-m-b-3',
            text: window.i18next.t('pmwjs_ai_videos'),
            onClick: handleAiVideosClick,
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail icon="icon-video-ai" />,
            },
          },
          {
            id: 'embed-video',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            subText: '',
            className: 'spacing-m-b-3',
            text: window.i18next.t('pmwjs_embed_videos'),
            onClick: handleEmbedVideoClick,
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail icon="icon-html" />,
            },
          },
        ]}
      />
    );
  };

  const getContent = (): ReactElement<any> => {
    if (isMobile) {
      return getSmallScreenContent();
    }
    return getLargeScreenContent();
  };

  return getContent();
}
